import { ClassValue } from 'clsx';
import { isEmpty } from 'lodash';
import { Text } from '@shared/components/Text';
import { AddressField } from '@shared/data/entity/fields/AddressField';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getAddressText } from './util/getAddressText';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: AddressField;
}

/**
 * Default: the street, then the city dimmed, each wrapping. Compact: the same two parts, each
 * truncating, on one line unless the host wraps them. Inline: one line of text.
 */
export function AddressCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }
  const isCompact = variant === 'compact';
  const isInline = variant === 'inline';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={className}
    >
      {isInline && <InlineAddress field={field} />}
      {isCompact && <CompactAddress field={field} />}
      {isDefault && <DefaultAddress field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineAddress({ field }: { field: AddressField }) {
  return <>{getAddressText(field)}</>;
}

function CompactAddress({ field }: { field: AddressField }) {
  const { title, subtitle } = field.address;

  return (
    <>
      <Text numberOfLines={1}>{title}</Text>
      {!isEmpty(subtitle) && (
        <Text weight="normal" inactive numberOfLines={1}>
          {subtitle}
        </Text>
      )}
    </>
  );
}

function DefaultAddress({ field }: { field: AddressField }) {
  const { title, subtitle } = field.address;

  return (
    <>
      <Text>{title}</Text>
      {!isEmpty(subtitle) && (
        <Text weight="normal" inactive>
          {subtitle}
        </Text>
      )}
    </>
  );
}
